/**
 * Conversation configuration for the rideshare drivers vertical.
 *
 * Single source of truth for what the WhatsApp agent collects,
 * in what order, and with what priority. The agent system prompt
 * is generated from this config via generateAgentSystemPrompt().
 */

import type { ConversationConfig } from '@selenos/leads/conversation';

import { SENDER_BRAND, SENDER_PERSON } from './opener-variants';

export const driversConversationConfig: ConversationConfig = {
  // No earnings figures in the greeting: specific hourly claims are marketing
  // territory (must be substantiated + current) and this is a service reply to
  // the user's own application. Mirrors the live 2026-07-02 prompt edit.
  // IDENTITY (Commerce Policy s.1): say who we are, in the first line, on the
  // reactive path too. This greeting previously named Uber and Bolt and never
  // named us at all, which reads as an official Uber onboarding channel: the
  // same "misrepresent your affiliation" exposure as the "this is the Uber Hero
  // team" copy that was live when +41765022800 was restricted. Rovvo is an
  // independent service that helps drivers get set up WITH Uber and Bolt. Name
  // the platforms only as the thing we help them join, never as us.
  // Persona + brand are the exported constants in `opener-variants.ts`, so the
  // agent and the proactive bodies cannot drift apart.
  greeting: `Hey! 👋 It's ${SENDER_PERSON} from ${SENDER_BRAND}, thanks for your application to drive. We help you get set up with Uber & Bolt, free. Just a few quick details to process it (takes 2 min) 🚗`,
  tone: `You are ${SENDER_PERSON}, a WhatsApp assistant for ${SENDER_BRAND}, an independent service that helps people get set up to drive with Uber and Bolt in South Africa. You do NOT work for Uber or Bolt and must never imply that you do. Warm, brief, encouraging. Match the user's casual SA register when they use "boet", "bra", "my bru" - keep it natural. Acknowledge each answer briefly (e.g. "Thanks, {name}!") before asking the next question.`,
  languageMatching: true,
  supportedLanguages: ['en', 'af', 'zu', 'xh'],
  defaultLanguage: 'en',

  fields: [
    {
      key: 'firstName',
      question: "What's your first name?",
      type: 'string',
      priority: 'required',
    },
    {
      key: 'lastName',
      question: 'And your last name?',
      type: 'string',
      priority: 'required',
    },
    {
      // Captured as natural language; the lead-capture pipeline normalizes
      // to `custom_fields.uber_territory_id` via `findZaTerritoryId` for
      // the Hero adapter (see packages/verticals/drivers/src/uber-territories.ts).
      // Required so we have city signal for downstream segmentation; if the
      // user's answer doesn't match any known ZA city the territory id falls
      // back to 0 (Uber accepts and uses the driver's account-known city).
      key: 'customFields.city',
      question: 'Which city do you drive in?',
      type: 'string',
      priority: 'required',
    },
    {
      // THE revenue-routing question (2026-07-06): only NEW-to-Uber drivers
      // generate the Hero referral payout; an existing driver is captured and
      // helped, but scores lower (see config.ts scoring) and is deprioritized
      // for delivery/nudges. Asked early so routing happens at capture time
      // instead of waiting for Uber's post-delivery membership classification
      // (PR #361). `important`, not `required`: a decline must never stall
      // the conversation. Skipped automatically for Instant-Form leads that
      // already answered it on the form (custom_fields.alreadyUberDriver set).
      key: 'customFields.alreadyUberDriver',
      question: 'Do you already drive with Uber?',
      type: 'boolean',
      priority: 'important',
    },
    {
      key: 'customFields.hasDriversLicense',
      question: "Do you have a valid South African driver's license?",
      type: 'boolean',
      priority: 'required',
      disqualifyWhen: {
        operator: 'eq',
        value: false,
        message:
          "Unfortunately a valid driver's license is required. Once you have one, reach out again!",
      },
    },
    {
      // Deliberately a SEPARATE question from the license (unbundled
      // 2026-07-06). Verified: Uber SA lists the PrDP as a mandatory UPLOAD
      // document (required for activation) but states no requirement to hold
      // one before starting signup, and DLTC issuance takes up to 12 weeks
      // (official ceiling). Bundling it into the license question (as the
      // pre-Jul-2026 Instant Form did) reads as "no PrDP = not eligible" and
      // scares off recoverable leads. No disqualifyWhen: "no" is a nurture
      // signal, not a rejection.
      // Sources: docs/investigations/uber-sa-driver-requirements/.
      key: 'customFields.hasPrDP',
      question: 'Do you have a Professional Driving Permit (PrDP)?',
      type: 'boolean',
      priority: 'required',
    },
    {
      // Work-eligibility lives in the CONVERSATION, never on the Meta Instant
      // Form: Meta's Discriminatory Practices policy (applies to EMPLOYMENT
      // ads globally; national origin is a protected attribute) makes a
      // work-authorization form question a MED-HIGH discrimination-flag risk,
      // and Meta's lead-form rules restrict government-ID collection. The
      // SA-ID/work-permit requirement itself is grade-C (secondary sources;
      // Uber's official documents page lists no ID item), but work
      // eligibility is a practical onboarding reality, so the agent asks
      // here (yes/no only, NEVER an ID or permit number), under the lead's
      // own application context.
      // Risk assessment: docs/investigations/uber-sa-driver-requirements/.
      key: 'customFields.hasSaIdOrWorkPermit',
      question:
        'Do you have a South African ID, or a passport with a valid work permit?',
      type: 'boolean',
      priority: 'important',
    },
    {
      key: 'customFields.hasSmartphone',
      question: 'Do you have a smartphone?',
      type: 'boolean',
      priority: 'required',
    },
    {
      // Asked AFTER the qualification questions, not before: requiring email
      // up-front cost leads (users balk before investing, then drop). `important`
      // (not `required`): the agent asks once and accepts a decline without
      // stalling or threatening to close the application. Email is still needed
      // for the Uber push — a lead that declines still qualifies and surfaces as
      // a recoverable `pending_email` at delivery rather than being lost here.
      key: 'email',
      question:
        'Last thing, what email should we use for your Uber application?',
      type: 'string',
      priority: 'important',
    },
    {
      // Scored (+15) AND a delivery prerequisite: without confirmed vehicle
      // access the lead soft-blocks `under_qualified` at delivery (see
      // delivery.qualificationGate.prerequisites in config.ts). Previously
      // form-only (never asked in-conversation), which left conversational
      // leads unanswerable at the delivery gate. `important`: asked once,
      // declinable, never stalls the conversation.
      key: 'customFields.hasVehicle',
      question: 'Do you have your own car, or access to one you can drive?',
      type: 'boolean',
      priority: 'important',
    },
    {
      // Scored (+10) — must be collected to qualify. `important`: the agent
      // asks, but the user may decline (recorded in custom_fields.__declined)
      // so the conversation never stalls. Was `optional`, which told the agent
      // never to ask → ~1% completeness → 10 scoring points unreachable.
      // Also a delivery prerequisite since 2026-07-06 (same gate as hasVehicle).
      key: 'customFields.hasInsurance',
      question: 'Do you have vehicle insurance?',
      type: 'boolean',
      priority: 'important',
    },
    {
      // Scored (+20) — same rationale as hasInsurance. Was `optional`.
      key: 'customFields.hasCleanRecord',
      question: 'Do you have a clean driving record?',
      type: 'boolean',
      priority: 'important',
    },
  ],

  consentPrompt:
    'Do you agree to being contacted by our recruitment team about your driver application?',
  completionMessage: `Thank you {{firstName}}! Your application has been submitted. The ${SENDER_BRAND} team will contact you within 24 hours.`,
  // Sent when the tool returns closeStyle "not_proceeding", i.e. the file is
  // closed and nobody will follow up. Deliberately gives NO reason: the status
  // comes from the scoring pipeline (`classification.ts`), the agent never knows
  // why, and the qualification data behind it is thin enough to be wrong. It
  // promises no future contact, and it leaves the door open on the one channel
  // that is actually open: this conversation. The previous wording ("you do not
  // meet the requirements") stated a verdict we cannot support to someone who
  // had just answered ten questions.
  disqualificationMessage:
    'Thanks for taking the time to go through all of that, {{firstName}}. I am not able to take your application forward right now. Your details stay on file, and you can message me here any time if anything changes.',

  // The old "City Earnings Data" table (R100-R150/hr per city) was removed
  // 2026-07-02 on the live agent and here 2026-07-04: volunteering earnings
  // figures turns a service conversation into an unsubstantiated marketing
  // claim. Repo had drifted behind the live prompt; keep them in sync.
  contextBlocks: [
    {
      label: 'Earnings questions (strict policy)',
      content: `NEVER volunteer earnings figures, hourly rates, or income claims (no "R150/hr", no "up to R...", no "start earning"). This conversation is a service reply to the user's own application, not a marketing pitch.
If the user ASKS about earnings: answer qualitatively and defer specifics, e.g. "It depends on your city and the hours you drive. The recruitment team will give you accurate local figures when they contact you." Then continue with the next question.`,
    },
    {
      label: 'No vehicle or no insurance (expectation setting)',
      content: `If the user says they have NO vehicle or NO vehicle insurance: record the answer, acknowledge it warmly, and continue with the remaining questions. Never dead-end the conversation or tell them to stop the application.
Set the expectation honestly in ONE sentence, e.g. "No stress, options like vehicle rental exist and we may follow up when there is a good fit for you." Do NOT promise a vehicle, a rental partner, financing, approval, or a timeline.`,
    },
    {
      label: 'Truthfulness (strict policy)',
      content: `NEVER claim information is "on file", "already provided", or that "we already have" something unless the user actually gave it in THIS conversation or the tool response confirms it. If you are not sure whether you have a piece of information, just ask for it.
Never state that the application is submitted, approved, or progressing unless the tool's mayComplete/nextQuestion state says so.`,
    },
  ],

  rules: [
    'NEVER send the greeting twice. If you have already greeted the user in this conversation, skip to the next question. Check the conversation history.',
    'If the user\'s FIRST message mentions "David", "driving", "Uber", "Bolt", "start", or the pre-filled CTWA text: do NOT ask for their name yet. Lead with the value and a one-tap first step, e.g. "Awesome, we get you set up to drive on Uber & Bolt, free. Ready to start? 🚗". Only after they reply yes/show interest (or tell you their city), ask "Great! What\'s your first name?". Asking for a name before giving any value makes cold clickers drop off.',
    'Acknowledge each user answer with one short phrase before the next question: "Thanks!" or "Got it, {name}!". Never ask 2 questions in one message.',
    'If asked what platform: "We help you get started on Uber and Bolt."',
    'Keep each message under 160 characters.',
    'If user responds in Afrikaans/Zulu/Xhosa, match their language. Otherwise English. SA casual register is fine.',
    'If user sends a confused response like "First name" or "Yes" without context, do NOT repeat the greeting. Simply say "No worries, can I get your first name?" and move forward.',
    'Ask for email only AFTER the qualification questions, never before. Ask once. If the user declines or has none, do NOT pressure them or threaten to close the application. Say "No problem, we can follow up about that later" and continue. Never block qualification on email.',
  ],
};
